import React from 'react';
import { AlertCircle, Calendar, Plus, X } from 'lucide-react';

function ResultsAddModal({
  darkMode = false,
  show,
  editMode = false,
  editingDate = null,
  formData,
  error = '',
  loading = false,
  onClose,
  onChange,
  onSubmit,
  onNoAction = null,
  showBaseInternal = true,
}) {
  if (!show) return null;

  const panelClass = darkMode
    ? 'bg-slate-900 border border-slate-800 shadow-xl shadow-slate-950/20'
    : 'bg-white shadow-sm';
  const inputClass = darkMode
    ? 'w-full px-3 py-2 border border-slate-700 bg-slate-950 text-slate-100 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const inputDisabledClass = darkMode
    ? 'bg-slate-900 text-slate-500 cursor-not-allowed'
    : 'bg-gray-100 text-gray-400 cursor-not-allowed';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${panelClass} rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className={`text-xl font-semibold ${darkMode ? 'text-slate-100' : 'text-gray-800'}`}>
            {editMode ? `Edit Result for ${editingDate}` : 'Add Results'}
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded-full transition-colors ${darkMode ? 'hover:bg-slate-800' : 'hover:bg-gray-100'}`}
          >
            <X className={`w-5 h-5 ${darkMode ? 'text-slate-400' : 'text-gray-500'}`} />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="bg-red-50 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div>
            <label className={`flex items-center space-x-2 text-sm font-medium mb-2 ${editMode ? 'text-gray-400' : 'text-gray-700'}`}>
              <Calendar size={16} />
              <span>Date</span>
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => onChange('date', e.target.value)}
              disabled={editMode}
              className={`${inputClass} ${editMode ? inputDisabledClass : ''}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              Base Action
            </label>
            <select
              value={formData.baseAction}
              onChange={(e) => onChange('baseAction', e.target.value)}
              className={inputClass}
            >
              <option value="Idle">Idle</option>
              <option value="Buy">Buy</option>
              <option value="Sell">Sell</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${formData.baseAction === 'Idle' ? 'text-gray-400' : 'text-gray-700'}`}>
              Base Price (PLN)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.basePrice}
              onChange={(e) => onChange('basePrice', parseFloat(e.target.value) || 0)}
              disabled={formData.baseAction === 'Idle'}
              className={`${inputClass} ${formData.baseAction === 'Idle' ? inputDisabledClass : ''}`}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${formData.baseAction === 'Idle' ? 'text-gray-400' : 'text-gray-700'}`}>
              Base Volume (MW)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.volume}
              onChange={(e) => onChange('volume', parseFloat(e.target.value) || 0)}
              disabled={formData.baseAction === 'Idle'}
              className={`${inputClass} ${formData.baseAction === 'Idle' ? inputDisabledClass : ''}`}
            />
          </div>

          {showBaseInternal && (
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isBaseActionInternal"
                checked={formData.isBaseActionInternal}
                onChange={(e) => onChange('isBaseActionInternal', e.target.checked)}
                disabled={formData.baseAction === 'Idle'}
                className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${formData.baseAction === 'Idle' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
              />
              <label
                htmlFor="isBaseActionInternal"
                className={`text-sm font-medium ${formData.baseAction === 'Idle' ? 'text-gray-400' : 'text-gray-700'}`}
              >
                Is Base Action Internal?
              </label>
            </div>
          )}

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              Bought (PLN)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.bought}
              onChange={(e) => onChange('bought', parseFloat(e.target.value) || 0)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              Sold (PLN)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.sold}
              onChange={(e) => onChange('sold', parseFloat(e.target.value) || 0)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              Total Volume (MWh)
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.totalVolume}
              onChange={(e) => onChange('totalVolume', parseFloat(e.target.value) || 0)}
              className={inputClass}
            />
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-gray-700'}`}>
              Comment
            </label>
            <textarea
              value={formData.comment}
              onChange={(e) => onChange('comment', e.target.value)}
              rows={3}
              className={`${inputClass} resize-none`}
              placeholder="Optional comment..."
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className={`flex-1 py-2 px-4 border font-medium rounded-lg transition-colors ${darkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-800' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            Cancel
          </button>
          {!editMode && onNoAction && (
            <button
              onClick={onNoAction}
              disabled={loading}
              className="flex-1 py-2 px-4 bg-gray-500 text-white font-medium rounded-lg hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              No Action
            </button>
          )}
          <button
            onClick={onSubmit}
            disabled={loading}
            className="flex-1 py-2 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>{editMode ? 'Saving...' : 'Adding...'}</span>
              </>
            ) : (
              <>
                {editMode ? (
                  <span>Save Changes</span>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Add Result</span>
                  </>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ResultsAddModal;
